import React, { useEffect, useState } from 'react';
import { ValidDataService } from '../../../../service/validDataService';
import { 
    Button, 
    TextField, 
    Typography, 
    FormControl, 
    InputLabel, 
    Select, 
    MenuItem, 
    List, 
    ListItem, 
    ListItemText, 
    ListItemSecondaryAction, 
    IconButton, 
    Box,
    Chip 
} from '@mui/material';
import DeleteIcon from '@mui/icons-material/Delete';
import EditIcon from '@mui/icons-material/Edit';
import ConfirmationDialog from '../../../ui/confirmationDialog';
import useAbortController from '../../../../hooks/useAbortController';
import useDeleteRecord from '../../../../hooks/useDeleteRecord';
import useDialogModal from '../../../../hooks/useDialogModal';

const manufacturerTypes = [
    { name: 'lenses', display: 'Lenses' },
    // { name: 'frames', display: 'Frames' },
    // { name: 'contacts', display: 'Contacts' },
    // { name: 'accessories', display: 'Accessories' },
];

const ManufacturerBrandList = ({ manufacturerId }) => {
    const [signal] = useAbortController();
    const [brand, setBrand] = useState('');
    const [type, setType] = useState('lenses');
    const [notes, setNotes] = useState('');
    const [editIndex, setEditIndex] = useState(-1);
    const [data, setData] = useState([]);
    const [loading, setLoading] = useState(false);

    const [
        ConfirmationDialogComp,
        showConfirmationDialog,
        closeConfirmationDialog,
    ] = useDialogModal(ConfirmationDialog);

    const { deleteRecords } = useDeleteRecord(showConfirmationDialog, closeConfirmationDialog);

    const fetchBrands = React.useCallback(async () => {
        if (!manufacturerId) return;
        
        try {
            setLoading(true);
            const response = await ValidDataService.getBrandsForManufacturer(
                manufacturerId,
                0,
                100,
                signal
            );
            if (response?.data?.data) {
                setData(response.data.data);
            }
        } catch (error) {
            console.error('Error fetching manufacturer brands:', error);
        } finally {
            setLoading(false);
        }
    }, [manufacturerId, signal]);

    useEffect(() => {
        fetchBrands();
    }, [fetchBrands]);

    const handleAddBrandToManufacturer = async () => {
        if (!brand.trim()) {
            return;
        }

        // Check for duplicates
        const duplicate = data.find(
            (item, index) => 
                item.brandName.toLowerCase() === brand.toLowerCase() && 
                item.type === type && 
                index !== editIndex
        );

        if (duplicate) {
            alert('This brand already exists for the selected type');
            return;
        }

        try {
            if (editIndex === -1) {
                // Add new brand
                const response = await ValidDataService.addBrandToManufacturer(manufacturerId, {
                    brandName: brand,
                    type,
                    notes,
                });
                
                if (response?.data) {
                    setData([...data, response.data]);
                }
            } else {
                // Update existing brand
                const brandToUpdate = data[editIndex];
                const response = await ValidDataService.addBrandToManufacturer(manufacturerId, {
                    id: brandToUpdate.id,
                    brandName: brand,
                    type,
                    notes,
                });
                
                if (response?.data) {
                    const updatedData = [...data];
                    updatedData[editIndex] = response.data;
                    setData(updatedData);
                    setEditIndex(-1);
                }
            }

            // Reset form
            setBrand('');
            setType('lenses');
            setNotes('');
        } catch (error) {
            console.error('Error saving brand:', error);
        }
    };

    const handleEditBrand = (index) => {
        const brandToEdit = data[index];
        setBrand(brandToEdit.brandName);
        setType(brandToEdit.type);
        setNotes(brandToEdit.notes || '');
        setEditIndex(index);
    };

    const handleCancelEdit = () => {
        setBrand('');
        setType('frames');
        setNotes('');
        setEditIndex(-1);
    };

    const deleteRecordHandler = (brandId) => {
        deleteRecords(
            [brandId],
            ValidDataService.deleteBrandFromManufacturer,
            (deletedIds) => {
                // Remove from local state after successful deletion
                setData(data.filter(item => item.id !== brandId));
            },
            (error) => {
                // onFailure callback
                console.error('Failed to delete brand:', error);
            }
        );
    };

    return (
        <Box>
            <Typography variant="h6" gutterBottom>
                Brands for this Manufacturer
            </Typography>

            <Box display="flex" alignItems="flex-start" gap={2} mb={2}>
                <FormControl sx={{ minWidth: 200 }} size='small'>
                    <InputLabel>Type</InputLabel>
                    <Select value={type} onChange={(e) => setType(e.target.value)}>
                        {manufacturerTypes.map((t) => (
                            <MenuItem key={t.name} value={t.name}>
                                {t.display}
                            </MenuItem>
                        ))}
                    </Select>
                </FormControl>

                <TextField
                    label="Brand Name"
                    value={brand}
                    onChange={(e) => setBrand(e.target.value)}
                    variant="outlined"
                    size="small"
                    style={{ minWidth: 250 }}
                />

                {/* <TextField
                    label="Notes (Optional)"
                    value={notes}
                    onChange={(e) => setNotes(e.target.value)}
                    variant="outlined"
                    size="small"
                    multiline
                    style={{ minWidth: 250 }}
                /> */}

                <Button
                    variant="contained"
                    color="primary"
                    onClick={handleAddBrandToManufacturer}
                    disabled={!brand.trim()}
                    sx={{ mt: 1 }}
                >
                    {editIndex === -1 ? 'Add Brand' : 'Update Brand'}
                </Button>

                {editIndex !== -1 && (
                    <Button
                        variant="outlined"
                        onClick={handleCancelEdit}
                        sx={{ mt: 1 }}
                    >
                        Cancel
                    </Button>
                )}
            </Box>

            {loading ? (
                <Typography>Loading brands...</Typography>
            ) : data.length === 0 ? (
                <Typography variant="body2" color="textSecondary">
                    No brands added yet. Add your first brand above.
                </Typography>
            ) : (
                <List sx={{ 
                    mt: 2, 
                    maxHeight: 400, 
                    overflow: 'auto', 
                    border: 1, 
                    borderColor: 'divider',
                    borderRadius: 1 
                }}>
                    {data.map((item, index) => (
                        <ListItem 
                            key={item.id} 
                            sx={{ 
                                '&:hover': { 
                                    backgroundColor: 'action.hover' 
                                } 
                            }}
                        >
                            <ListItemText
                                primary={
                                    <Box display="flex" alignItems="center">
                                        <Chip
                                            label={item.type}
                                            size="small"
                                            color="primary"
                                            sx={{ mr: 1, textTransform: 'capitalize' }}
                                        />
                                        <Typography sx={{ fontWeight: 500 }}>
                                            {item.brandName}
                                        </Typography>
                                    </Box>
                                }
                                secondary={item.notes}
                            />
                            <ListItemSecondaryAction>
                                <IconButton
                                    edge="end"
                                    aria-label="edit"
                                    onClick={() => handleEditBrand(index)}
                                    size="small"
                                    style={{ marginRight: 8 }}
                                >
                                    <EditIcon fontSize="small" />
                                </IconButton>
                                <IconButton
                                    edge="end"
                                    aria-label="delete"
                                    onClick={() => deleteRecordHandler(item.id)}
                                    size="small"
                                >
                                    <DeleteIcon fontSize="small" />
                                </IconButton>
                            </ListItemSecondaryAction>
                        </ListItem>
                    ))}
                </List>
            )}

            <ConfirmationDialogComp
                label={{
                    positive: "Confirm",
                    negative: "Cancel",
                }} />
        </Box>
    );
};

export default ManufacturerBrandList;
